import { notEmpty } from '@ember/object/computed';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { modelApiAction } from 'cio/utils/model-api-actions';
import LayoutModel from 'cio/models/layout';
import IdentityModel from 'cio/models/identity';
import ActionModel from 'cio/models/action';
import NewsletterModel from 'cio/models/newsletter';
import TransactionalMessageModel from './transactional-message';
import { Editor } from 'cio/services/editor';
import { sanitize } from 'cio/utils/sanitize';
import { config } from 'cio/helpers/preview-whitelist';
import { BatchUpdateTemplateType } from 'cio/utils/actions/batch-update';
import { Header } from 'cio/utils/models/header';

const NO_PREPROCESSOR = 'none';
const DEFAULT_PREPROCESSOR = 'premailer';

export const DEFAULT_PAGE_SIZE = 25;

export type TemplateType = BatchUpdateTemplateType | string;

export default class TemplateModel extends Model {
  @service store!: DS.Store;

  @attr() bcc!: unknown;
  @attr('string') body!: string;
  @attr('string') bodyJson!: string;
  @attr('string') bodyAmp!: string;
  @attr() created!: unknown;
  @attr() editor!: Editor;
  @attr() fakeBcc!: unknown;
  @attr('string') language!: string;
  @attr('string') name!: string;
  @attr() preheaderText!: unknown;
  @attr('string') preprocessor!: string;
  @attr() recipient!: unknown;
  @attr('string') requestMethod!: string;
  @attr('string') subject!: string;
  @attr('string') imageUrl!: string;
  @attr('string') templateType!: TemplateType;
  @attr('number') templateEngine!: number;
  @attr('string') testGroupId!: string;
  @attr() updated!: unknown;
  @attr('string') url!: string;
  @attr('boolean') whatsapp!: boolean;

  // Number rather than relationship, because we don't need the relationship in
  // Ember Data, plus belongsTo associations don't show in hasDirtyAttributes
  // (which we use for the save bar)
  @attr('number') recipientEnvironmentId!: number;

  @attr('array', { defaultValue: () => [] }) headers!: Header[];

  // Used for transactional messages, has a list of the variables in the template.
  @attr('array') variables!: string[];

  @belongsTo('layout', { async: true, inverse: 'templates' }) layout!: DS.PromiseObject<LayoutModel> & LayoutModel;
  @attr('string') fromIdentityId?: string;
  @attr('string') replyToIdentityId?: string;

  @belongsTo('action', { async: false, inverse: 'template' }) action!: ActionModel;
  @belongsTo('newsletter', { async: false, inverse: 'templates' }) newsletter!: NewsletterModel;
  @belongsTo('transactional-message', { async: false, inverse: 'templates' })
  transactionalMessage!: TransactionalMessageModel;
  @belongsTo('identity', { async: false, inverse: null }) replyToIdentity!: IdentityModel;

  get templateEngineName() {
    return this.templateEngine === 1 ? 'Latest' : 'Legacy';
  }

  // "fromIdentity" must be a computed property because "from_identity_id" can point at deleted identity that is not in Ember Data store: https://github.com/customerio/opsbugs/issues/1302
  // this happens when Twilio sender identity used by existing Twilio actions is deleted
  @computed('fromIdentityId', {
    set(_key, value: IdentityModel | null) {
      let newId = value ? value.id : null;
      if (this.fromIdentityId !== newId) {
        this.set('fromIdentityId', newId);
      }

      return value;
    },
    get() {
      if (this.fromIdentityId) {
        return this.store.peekRecord('identity', this.fromIdentityId);
      }
      return null;
    },
  })
  fromIdentity!: IdentityModel;

  @computed('preprocessor', {
    get() {
      return this.preprocessor === NO_PREPROCESSOR;
    },

    set(_key, value) {
      let disabled = !!value;

      this.set('preprocessor', disabled ? NO_PREPROCESSOR : DEFAULT_PREPROCESSOR);

      return disabled;
    },
  })
  disablePreprocessor!: unknown;

  @notEmpty('subject') hasSubject!: boolean;
  @notEmpty('layout') hasLayout!: boolean;
  @notEmpty('fromIdentity') hasFromIdentity!: boolean;
  @notEmpty('body') hasContent!: boolean;
  @notEmpty('bodyJson') hasJsonContent!: boolean;
  @notEmpty('url') hasUrl!: boolean;
  @notEmpty('bcc') hasBcc!: boolean;

  @computed('body')
  get sanitizedBody() {
    return sanitize(this.body, config);
  }

  @computed('editor', 'hasSubject', 'hasContent', 'hasJsonContent', 'templateType')
  get readyToSend() {
    switch (this.templateType) {
      case 'email':
        return this.hasContent && this.hasSubject;
      case 'push':
        return this.editor === 'json' ? this.hasJsonContent : this.hasContent;
      case 'twilio':
        return this.hasContent;
      case 'webhook':
        return this.hasUrl;
      case 'in_app':
        return this.hasJsonContent;
      default:
        return this.hasContent && this.hasSubject;
    }
  }

  fetchPreviewVariables = modelApiAction({
    type: 'GET',
    path: 'variables',
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    template: TemplateModel;
  }
}
